<template>
    <b-modal
        ref="dialog"
        lazy
        title-tag="h2"
        title="Check Out"
        footer-class="d-flex"
        @show="$emit('loaded')"
        no-close-on-backdrop
        no-close-on-esc
    >
        <b-form-group label-cols="3" label="Check In:" label-for="check-in">
            <b-form-input plaintext :value="checkIn" />
        </b-form-group>

        <b-form-group label-cols="3" label="Check Out:" label-for="check-out">
            <div class="checkout-wrapper">
                <date-picker
                    name="checkoutDate"
                    :config="datePickerOptions"
                    v-model="checkoutDate"
                    @dp-change="changeCheckoutDate"
                    placeholder="Date (required)"
                />
                <time-picker
                    v-model="checkoutTime"
                    placeholder="Time (required)"
                    name="checkoutTime"
                    format="moment"
                    class="form-control"
                />
            </div>
        </b-form-group>

        <b-form-group label-cols="3" label="Notes:" label-for="notes">
            <textarea
                class="form-control"
                placeholder="Notes (optional)"
                v-model="notes"
            />
        </b-form-group>

        <b-form-group
            label-cols="3"
            label="Medical Notes:"
            label-for="medical-notes"
        >
            <b-form-input plaintext :value="medicalNotes || '--'" />
        </b-form-group>

        <b-form-group label-cols="3" label="Form:" label-for="form">
            <b-form-input plaintext :value="form || '--'" />
        </b-form-group>

        <b-form-group
            label-cols="3"
            label="Invoice:"
            label-for="invoice"
            class="custom-flex"
        >
            <router-link
                v-if="invoicePath"
                :to="`/billing/invoice/${invoicePath}`"
            >
                {{ invoiceName }}
            </router-link>
            <span v-else>{{ invoiceName }}</span>
        </b-form-group>
        <template v-slot:modal-footer="{ close }">
            <loader v-if="isSaving" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSaving"
            >
                Cancel
            </b-button>

            <b-button v-if="!isSaving" variant="primary" @click="save">
                Check Out
            </b-button>
        </template>

        <ModalConfirmExtend
            ref="modalConfirmExtend"
            @proceed="proceedCheckout"
            @cancel="isSaving = false"
        />
    </b-modal>
</template>

<script>
import BoardingService from "@/services/boarding.js";
import Loader from "@/module/commons/loader";
import TimePicker from "@/module/commons/time-picker";
import ModalConfirmExtend from "@/module/schedule/boarding/modal-check-out-confirm";

export default {
    components: {
        Loader,
        TimePicker,
        ModalConfirmExtend
    },
    data() {
        return {
            checkIn: null,
            checkoutDate: null,
            checkoutDateStr: "",
            checkoutTime: null,
            notes: null,
            medicalNotes: null,
            form: null,
            invoiceName: null,
            invoicePath: null,
            isSaving: false,
            boardingId: null,
            datePickerOptions: {}
        };
    },
    methods: {
        changeCheckoutDate(value) {
            this.checkoutDateStr = value.date.format("YYYYMMDD");
        },
        async open(id) {
            await this._getSetting();
            this.datePickerOptions = {
                format: this.getDateFormat(),
                timeZone: this._timeZone
            };

            let details = await BoardingService.getBoardingById(id);

            this.boardingId = id;
            this.checkIn = moment
                .unix(details.start)
                .tz(this._timeZone)
                .format(this.getDateFormat() + ", " + "HH:mm A");

            let momentCheckOut = moment().tz(this._timeZone);
            this.checkoutDate = momentCheckOut.clone();
            this.checkoutDateStr = momentCheckOut.format("YYYYMMDD");
            this.checkoutTime = momentCheckOut.clone();
            this.medicalNotes = details?.soap?.template?.name ?? "--";
            this.form = details?.letter?.title ?? "--";
            this.invoiceName = details?.invoice?.name;
            this.invoicePath = details?.invoice?.encoded_id ?? null;
            this.notes = details.note;

            this.$refs.dialog.show();
        },
        async save() {
            try {
                let date = this.checkoutDateStr;
                let time = this.checkoutTime.format("HHmm");
                let datetimeMoment = moment.tz(
                    date + time,
                    "YYYYMMDDHHmm",
                    this._timeZone
                );
                let checkoutUnix = datetimeMoment.unix();
                let checkoutData = {
                    id: this.boardingId,
                    datetime: checkoutUnix,
                    note: this.notes
                };

                this.isSaving = true;
                let preCheckout = await BoardingService.preCheckOut(
                    checkoutData
                );

                if (preCheckout?.response?.code !== 200) {
                    throw new Error("Error: Unable to proceed (Pre-checkout)");
                }

                let preCheckOutResult =
                    preCheckout?.response?.message?.formData ?? {};

                let {
                    item_amount: itemAmount = 0,
                    invoice_balance: invoiceBalance = 0,
                    credit_amount: creditAmount = 0,
                    payment_amount: paymentAmount = 0,
                    invoice_status: invoiceStatus,
                    extend_boarding: extendBoarding,
                    original_days: originalDays = 0,
                    extend_days: extendDays
                } = preCheckOutResult;

                let itemTotalInCents = itemAmount * 100;
                let invoiceBalanceInCents = invoiceBalance * 100;
                let creditAmountInCents = creditAmount * 100;
                let paymentAmountInCents = paymentAmount * 100;

                if (invoiceBalanceInCents < itemTotalInCents) {
                    this.isSaving = false;
                    this.$refs.modalConfirmExtend.open({
                        type: "INVOICE_OVERPAID_UNDERPAID",
                        creditAmount: creditAmountInCents,
                        paymentAmount: paymentAmountInCents,
                        checkoutData: checkoutData
                    });
                } else if (
                    invoiceStatus === 2 &&
                    extendBoarding &&
                    extendDays > 0
                ) {
                    this.isSaving = false;
                    this.$refs.modalConfirmExtend.open({
                        type: "INVOICE_PAID_EXTEND",
                        actualNights: originalDays,
                        extendedNights: extendDays,
                        actualCheckoutDate: datetimeMoment,
                        checkoutData: checkoutData
                    });
                } else {
                    this.proceedCheckout(checkoutData);
                }
            } catch (error) {
                this.isSaving = false;
                this._message(error.message, "danger");
            }
        },
        async proceedCheckout(checkoutData) {
            try {
                this.isSaving = true;
                await BoardingService.checkOut(checkoutData);
                this._message("Reservation successfully checked out");
                this.$refs.dialog.hide();
                this.$emit("success", this.boardingId);
            } catch (error) {
                this.isSaving = false;
                this._message(error.message, "danger");
            }
        }
    }
};
</script>

<style scoped>
::v-deep .custom-flex .bv-no-focus-ring {
    display: flex;
    align-items: center;
}
.checkout-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 10px;
}
</style>
